import '../../styles/restScreens.css';
import React, { useState, useCallback, useMemo } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

const ResponsiveGridLayout = WidthProvider(Responsive);

/**
 * DashboardGrid Component
 *
 * Main grid layout container for dashboard widgets.
 * Handles drag-and-drop, resizing, and responsive layouts.
 */
const DashboardGrid = ({
  widgets = [],
  onLayoutChange,
  onWidgetRemove,
  onWidgetConfigure,
  onGroupWidgets,
  isEditing = false,
  renderWidget
}) => {
  const [mounted, setMounted] = useState(false);
  const [draggingWidget, setDraggingWidget] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);

  // Grid configuration
  const cols = { lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 };
  const rowHeight = 60;
  const breakpoints = { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 };

  // Convert widgets to layout format
  const layout = useMemo(() => {
    return widgets.map(widget => {
      const settings = widget.settings || {};
      const isLocked = settings.locked || false;
      const isStatic = settings.static || false;
      const canResize = settings.canResize !== false; // default true
      const canRelocate = settings.canRelocate !== false; // default true

      return {
        i: widget.id,
        x: widget.x || 0,
        y: widget.y || 0,
        w: widget.w || 4,
        h: widget.h || 3,
        minW: widget.minW || 2,
        minH: widget.minH || 2,
        // Widget is static if: not in editing mode, OR explicitly set to static, OR locked
        static: !isEditing || isStatic || isLocked,
        // Disable resize if: not in editing mode, OR can't resize, OR locked, OR static
        isResizable: isEditing && canResize && !isLocked && !isStatic,
        // Disable drag if: not in editing mode, OR can't relocate, OR locked, OR static
        isDraggable: isEditing && canRelocate && !isLocked && !isStatic
      };
    });
  }, [widgets, isEditing]);

  // Handle layout change
  const handleLayoutChange = useCallback((newLayout, allLayouts) => {
    if (!mounted) {
      setMounted(true);
      return;
    }

    if (onLayoutChange) {
      // Convert layout back to widget format
      const updatedWidgets = widgets.map(widget => {
        const layoutItem = newLayout.find(item => item.i === widget.id);
        if (layoutItem) {
          return {
            ...widget,
            x: layoutItem.x,
            y: layoutItem.y,
            w: layoutItem.w,
            h: layoutItem.h
          };
        }
        return widget;
      });
      onLayoutChange(updatedWidgets);
    }
  }, [widgets, onLayoutChange, mounted]);

  // Handle widget remove
  const handleWidgetRemove = useCallback((widgetId) => {
    if (onWidgetRemove) {
      onWidgetRemove(widgetId);
    }
  }, [onWidgetRemove]);

  // Handle widget configure
  const handleWidgetConfigure = useCallback((widgetId) => {
    if (onWidgetConfigure) {
      onWidgetConfigure(widgetId);
    }
  }, [onWidgetConfigure]);

  // Calculate bounding box overlap for grouping detection
  const calculateOverlap = useCallback((layout1, layout2) => {
    const x1 = Math.max(layout1.x, layout2.x);
    const x2 = Math.min(layout1.x + layout1.w, layout2.x + layout2.w);
    const y1 = Math.max(layout1.y, layout2.y);
    const y2 = Math.min(layout1.y + layout1.h, layout2.y + layout2.h);

    const overlapWidth = Math.max(0, x2 - x1);
    const overlapHeight = Math.max(0, y2 - y1);
    const overlapArea = overlapWidth * overlapHeight;

    const area1 = layout1.w * layout1.h;
    const area2 = layout2.w * layout2.h;
    const minArea = Math.min(area1, area2);

    // Return overlap percentage (0-1)
    return minArea > 0 ? overlapArea / minArea : 0;
  }, []);

  // Handle drag start - track which widget is being dragged
  const handleDragStart = useCallback((layout, oldItem, newItem, placeholder, e, element) => {
    setDraggingWidget(newItem.i);
    setDropTarget(null);
  }, []);

  // Handle drag - check for overlap with other widgets
  const handleDrag = useCallback((layout, oldItem, newItem, placeholder, e, element) => {
    if (!onGroupWidgets) return;

    // Find widgets that overlap with the dragged widget
    let maxOverlap = 0;
    let targetWidget = null;

    widgets.forEach(widget => {
      if (widget.id === newItem.i) return; // Skip self
      // Allow grouping with existing groups for multi-widget stacking

      const widgetLayout = layout.find(l => l.i === widget.id);
      if (!widgetLayout) return;

      const overlap = calculateOverlap(newItem, widgetLayout);

      // Require at least 20% overlap to trigger grouping (easier stacking)
      if (overlap > 0.2 && overlap > maxOverlap) {
        maxOverlap = overlap;
        targetWidget = widget.id;
      }
    });

    setDropTarget(maxOverlap > 0.2 ? targetWidget : null);
  }, [widgets, onGroupWidgets, calculateOverlap]);

  // Handle drag stop - create group if dropped on another widget
  const handleDragStop = useCallback((layout, oldItem, newItem, placeholder, e, element) => {
    const droppedOnWidget = dropTarget;
    setDraggingWidget(null);
    setDropTarget(null);

    if (droppedOnWidget && onGroupWidgets) {
      // Create a group from the two widgets
      onGroupWidgets(newItem.i, droppedOnWidget);
    }
  }, [dropTarget, onGroupWidgets]);

  return (
    <div data-m6-layout="widgets" data-m6-component="DashboardGrid" className="dashboard-grid-container">
      {/* Editing mode indicator */}
      {isEditing && (
        <div className="mb-4 p-3 bg-[var(--nav-active-bg)] border border-[var(--nav-active-edge)]/30 rounded-lg">
          <p className="text-sm text-[var(--nav-active-ink)] flex items-center font-medium">
            <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
            </svg>
            Editing Mode: Drag widgets to rearrange, resize by dragging corners
          </p>
        </div>
      )}

      {/* Grid Layout */}
      <ResponsiveGridLayout
        className="layout"
        layouts={{ lg: layout }}
        breakpoints={breakpoints}
        cols={cols}
        rowHeight={rowHeight}
        onLayoutChange={handleLayoutChange}
        onDragStart={handleDragStart}
        onDrag={handleDrag}
        onDragStop={handleDragStop}
        isDraggable={isEditing}
        isResizable={isEditing}
        compactType="vertical"
        preventCollision={false}
        margin={[16, 16]}
        containerPadding={[0, 0]}
        useCSSTransforms={true}
        draggableHandle=".widget-drag-handle"
      >
        {widgets.map(widget => {
          const isDropTarget = dropTarget === widget.id;

          return (
            <div
              key={widget.id}
              className={`grid-item ${isDropTarget ? 'drop-target' : ''}`}
            >
              {renderWidget ? (
                renderWidget(widget, {
                  onRemove: () => handleWidgetRemove(widget.id),
                  onConfigure: () => handleWidgetConfigure(widget.id),
                  isEditing
                })
              ) : (
                <div className="bg-[var(--surface-raised)] rounded-lg shadow p-4 h-full">
                  <p className="text-sm text-[var(--ink-muted)]">Widget: {widget.type}</p>
                </div>
              )}
            </div>
          );
        })}
      </ResponsiveGridLayout>

      {/* Empty state */}
      {widgets.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <svg className="w-16 h-16 text-[var(--ink-muted)] mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
          </svg>
          <h3 className="text-lg font-medium text-[var(--ink)] mb-2">
            No widgets added yet
          </h3>
          <p className="text-sm text-[var(--ink-muted)] mb-4">
            Click "Add Widget" to get started with your custom dashboard
          </p>
        </div>
      )}

      {/* Custom styles for grid */}
      <style>{`
        .dashboard-grid-container {
          width: 100%;
        }

        .grid-item {
          transition: all 200ms ease;
        }

        .grid-item.drop-target > * {
          border: 3px solid var(--accent-ink);
          animation: pulse-border 1s ease-in-out infinite;
        }

        @keyframes pulse-border {
          0%, 100% {
            border-color: var(--accent-ink);
            box-shadow: 0 0 0 0 rgb(var(--brand-500) / 0.45);
          }
          50% {
            border-color: rgb(var(--brand-400));
            box-shadow: 0 0 0 8px rgb(var(--brand-500) / 0);
          }
        }

        .grid-item.react-grid-placeholder {
          background: rgb(var(--brand-500) / 0.15);
          border: 2px dashed var(--accent-ink);
          border-radius: 8px;
          transition-duration: 100ms;
          z-index: 2;
        }

        .react-grid-item.react-grid-placeholder {
          background: rgb(var(--brand-500) / 0.15);
        }

        .react-grid-item.resizing {
          opacity: 0.9;
          transition: none;
        }

        .react-grid-item.static {
          background: transparent;
        }

        .react-grid-item > .react-resizable-handle {
          opacity: 0;
          transition: opacity 200ms;
        }

        .react-grid-item:hover > .react-resizable-handle {
          opacity: 1;
        }

        .react-grid-item > .react-resizable-handle::after {
          content: "";
          position: absolute;
          right: 3px;
          bottom: 3px;
          width: 8px;
          height: 8px;
          border-right: 2px solid rgba(59, 130, 246, 0.8);
          border-bottom: 2px solid rgba(59, 130, 246, 0.8);
        }

        .widget-drag-handle {
          cursor: ${isEditing ? 'move' : 'default'};
        }
      `}</style>
    </div>
  );
};

export default DashboardGrid;
